import type { ColorName } from '@tunasong/schemas'
import { materialColors, type ColorShade } from './material-colors.js'

const colors = [
  'amber',
  'blue',
  'blueGrey',
  'brown',
  'cyan',
  'deepOrange',
  'deepPurple',
  'green',
  'grey',
  'indigo',
  'lightBlue',
  'lightGreen',
  'lime',
  'orange',
  'pink',
  'purple',
  'red',
  'teal',
  'yellow',
]

const excludeColors = ['grey', 'blueGrey']

export const colorNames = colors
  .filter(c => !excludeColors.includes(c))
  .sort()
  .reverse() as readonly ColorName[]

/** Get color value from color name */
export const getColorCSS = (colorName?: ColorName, intensity: ColorShade = 500) =>
  colorName ? materialColors[colorName][intensity] : 'inherit'

/**
 * The light-mode shade of an icon whose shade 500 is under 3:1 on white or paper (GH #2558): the lowest darker shade
 * that holds on both, chosen per colour, since contrast is not monotonic across Material's table (700 is lighter than
 * 600 for green, cyan and lightGreen).
 *
 * Amber and yellow are the exception, and on purpose: no Material shade of either reaches 3:1 on white (900 is 2.79
 * and 2.65, 2.72 and 2.58 on paper). They use 900, as dark as the family goes, and stay recognisably the colour the
 * user chose for the entity. Joni (GH #2558): an icon that sits beside its visible name is not needed to identify the
 * thing, so WCAG 1.4.11 does not apply. Don't "fix" them into brown or grey; an icon-only use needs its own label.
 */
const lightIconShades: Partial<Record<ColorName, ColorShade>> = {
  green: 600,
  /** The default plugin colour, left out of the picker but common on icons (#2579) */
  grey: 600,
  lightBlue: 700,
  cyan: 800,
  lightGreen: 800,
  orange: 900,
  lime: 900,
  amber: 900,
  yellow: 900,
}

/**
 * Color for an icon or other foreground mark on the page. Dark: shade 300, where 500 is under 3:1 for indigo,
 * deepPurple, brown and purple (GH #2529). Light: shade 500, or the darker shade in lightIconShades (GH #2558).
 * light-dark() follows the theme's color-scheme (tokens.css). Set it as a CSS `color` (style), not an SVG attribute,
 * which does not take CSS functions. Fills (avatars, tags, swatches) keep getColorCSS's shade 500.
 */
export const getIconColorCSS = (colorName?: ColorName) =>
  colorName
    ? `light-dark(${materialColors[colorName][lightIconShades[colorName] ?? 500]}, ${materialColors[colorName][300]})`
    : 'inherit'
